import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  Collapse
} from '@mui/material';

import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as PreviewIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  FileCopy as TemplateIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Help as HelpIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import axiosInstance from '../services/axiosInstance';
import { IAsset, ITemplate, ICategory } from '../types/damTypes';

interface CsvMapping {
  csvColumn: string;
  assetField: string;
  fieldType: 'basic' | 'metadata' | 'category' | 'tag';
  transform?: 'none' | 'split' | 'join' | 'date' | 'number' | 'boolean';
  separator?: string;
}

interface SavedTemplate {
  _id?: string;
  name: string;
  description: string;
  type: 'import' | 'export';
  mappings: CsvMapping[];
  settings: {
    delimiter: string;
    encoding: string;
    hasHeader: boolean;
    dateFormat?: string;
    selectedFields?: string[];
  };
  createdAt?: Date;
  updatedAt?: Date;
}

interface CsvPreviewData {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

interface AvailableField {
  id: string;
  name: string;
  type: 'basic' | 'metadata' | 'category' | 'tag';
  description: string;
  isKey: boolean;
  templateName?: string;
  fieldType?: string;
  fieldName?: string; // System field name
  fieldLabel?: string; // Display field label
  fieldId?: string; // Template field ID
}

interface PendingTemplateNewColumns {
  template: SavedTemplate;
  missingHeaders: string[];
  baseMappings: CsvMapping[];
  signature: string;
}

const CsvImportExport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'import' | 'export'>('import');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvPreviewData | null>(null);
  const [mappings, setMappings] = useState<CsvMapping[]>([]);
  const [savedTemplates, setSavedTemplates] = useState<SavedTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<SavedTemplate | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Template management
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<SavedTemplate | null>(null);
  
  // Available fields from system
  const [availableFields, setAvailableFields] = useState<AvailableField[]>([]);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  
  // Export settings
  const [exportSettings, setExportSettings] = useState({
    includeBasicFields: true,
    includeMetadata: true,
    includeCategories: true,
    includeTags: true,
    selectedFields: [] as string[],
    delimiter: ',',
    encoding: 'utf-8',
    dateFormat: 'YYYY-MM-DD'
  });

  // Color coding toggle for export fields (default: enabled)
  const [colorCodeFields, setColorCodeFields] = useState(true);

  // Color definitions for field types and templates
  const fieldTypeColors: Record<string, { bg: string; border: string }> = {
    // Base field types
    basic: { bg: '#f0f7ff', border: '#4a90d9' },      // Light blue
    tag: { bg: '#f0fff4', border: '#48bb78' },        // Light green
    category: { bg: '#fff8f0', border: '#ed8936' },   // Light orange/yellow
    // Template-specific metadata colors
    'metadata:Produkter': { bg: '#f5f0ff', border: '#805ad5' },      // Light purple/lavender
    'metadata:Vin': { bg: '#fff0f5', border: '#d53f8c' },            // Light pink/wine
    'metadata:Mat': { bg: '#fff5f0', border: '#e57345' },            // Light peach
    'metadata:Butiksmaterial': { bg: '#f0fff8', border: '#38a169' }, // Light mint
    // Default metadata color for other templates
    metadata: { bg: '#faf5ff', border: '#9f7aea' },   // Light violet
  };

  // Get color for a field based on type and templateName
  const getFieldColor = (field: AvailableField): { bg: string; border: string } => {
    if (!colorCodeFields) {
      return { bg: '#ffffff', border: '#e0e0e0' };
    }
    
    if (field.type === 'metadata' && field.templateName) {
      const templateKey = `metadata:${field.templateName}`;
      if (fieldTypeColors[templateKey]) {
        return fieldTypeColors[templateKey];
      }
      // Generate a consistent color for unknown templates based on template name
      const hash = field.templateName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const hue = hash % 360;
      return { 
        bg: `hsl(${hue}, 40%, 97%)`, 
        border: `hsl(${hue}, 50%, 55%)` 
      };
    }
    
    return fieldTypeColors[field.type] || { bg: '#ffffff', border: '#e0e0e0' };
  };

  // Import settings
  const [importSettings, setImportSettings] = useState({
    delimiter: ';',
    encoding: 'utf-8',
    hasHeader: true,
    updateExisting: true,
    createMissing: false,
    keyField: 'title', // Field to use for matching existing assets
    dryRun: false
  });

  // Import page UI state
  const [transformHelpExpanded, setTransformHelpExpanded] = useState(false);
  const [pendingTemplateNewColumns, setPendingTemplateNewColumns] = useState<PendingTemplateNewColumns | null>(null);
  const [handledTemplateNewColumnPrompts, setHandledTemplateNewColumnPrompts] = useState<string[]>([]);

  // Calculate mapping statistics
  const getMappingStats = () => {
    const total = mappings.length;
    const mapped = mappings.filter(m => m.assetField && m.assetField !== '').length;
    const unmapped = total - mapped;
    return { total, mapped, unmapped };
  };

  // Get color for a mapping row based on its status
  const getMappingRowColor = (mapping: CsvMapping): string => {
    if (!mapping.assetField || mapping.assetField === '') {
      return '#fff3e0'; // Light orange for unmapped
    }
    const field = availableFields.find(f => f.id === mapping.assetField);
    if (field) {
      const colors = getFieldColor(field);
      return colors.bg;
    }
    return '#e8f5e9'; // Light green for mapped (default)
  };

  // Get border color for a mapping row
  const getMappingRowBorder = (mapping: CsvMapping): string => {
    if (!mapping.assetField || mapping.assetField === '') {
      return '#ff9800'; // Orange for unmapped
    }
    const field = availableFields.find(f => f.id === mapping.assetField);
    if (field) {
      const colors = getFieldColor(field);
      return colors.border;
    }
    return '#4caf50'; // Green for mapped (default)
  };

  const normalizeCsvColumn = (column: string) => column.trim().toLowerCase();

  const findMappingByCsvColumn = (mappingList: CsvMapping[], csvColumn: string) => {
    const normalizedColumn = normalizeCsvColumn(csvColumn);
    return mappingList.find(mapping => normalizeCsvColumn(mapping.csvColumn) === normalizedColumn);
  };

  const getTemplateNewColumnSignature = (template: SavedTemplate, headers: string[]) => {
    const templateId = template._id || template.name;
    const normalizedHeaders = headers.map(normalizeCsvColumn).sort().join('|');
    return `${templateId}:${normalizedHeaders}`;
  };

  const getMissingHeadersForTemplate = (template: SavedTemplate, headers: string[]) => {
    const templateColumns = new Set(template.mappings.map(mapping => normalizeCsvColumn(mapping.csvColumn)));
    return headers.filter(header => !templateColumns.has(normalizeCsvColumn(header)));
  };

  const createUnmappedMappings = (headers: string[]): CsvMapping[] => headers.map(header => ({
    csvColumn: header,
    assetField: '',
    fieldType: 'basic',
    transform: 'none'
  }));

  const markTemplateNewColumnPromptHandled = (signature: string) => {
    setHandledTemplateNewColumnPrompts(previous =>
      previous.includes(signature) ? previous : [...previous, signature]
    );
  };

  const applyImportTemplateMappings = (
    template: SavedTemplate,
    preview: CsvPreviewData | null,
    options: { preserveExistingCsvMappings?: boolean } = {}
  ) => {
    const templateMappings = template.mappings || [];

    if (!preview?.headers?.length || !importSettings.hasHeader) {
      setMappings(templateMappings);
      return;
    }

    const missingHeaders = getMissingHeadersForTemplate(template, preview.headers);
    const preservedMissingMappings = options.preserveExistingCsvMappings
      ? missingHeaders
          .map(header => findMappingByCsvColumn(mappings, header))
          .filter((mapping): mapping is CsvMapping => Boolean(mapping))
      : [];
    const preservedColumns = new Set(preservedMissingMappings.map(mapping => normalizeCsvColumn(mapping.csvColumn)));
    const headersNeedingDecision = missingHeaders.filter(header => !preservedColumns.has(normalizeCsvColumn(header)));
    const baseMappings = [...templateMappings, ...preservedMissingMappings];
    const signature = getTemplateNewColumnSignature(template, preview.headers);

    setMappings(baseMappings);

    if (headersNeedingDecision.length > 0 && !handledTemplateNewColumnPrompts.includes(signature)) {
      setPendingTemplateNewColumns({
        template,
        missingHeaders: headersNeedingDecision,
        baseMappings,
        signature
      });
    }
  };

  const confirmAddTemplateNewColumns = () => {
    if (!pendingTemplateNewColumns) return;

    setMappings([
      ...pendingTemplateNewColumns.baseMappings,
      ...createUnmappedMappings(pendingTemplateNewColumns.missingHeaders)
    ]);
    markTemplateNewColumnPromptHandled(pendingTemplateNewColumns.signature);
    setPendingTemplateNewColumns(null);
    setSuccess(
      `${pendingTemplateNewColumns.missingHeaders.length} new CSV column${pendingTemplateNewColumns.missingHeaders.length === 1 ? '' : 's'} added as unmapped rows`
    );
  };

  const dismissTemplateNewColumns = () => {
    if (!pendingTemplateNewColumns) return;

    markTemplateNewColumnPromptHandled(pendingTemplateNewColumns.signature);
    setPendingTemplateNewColumns(null);
  };

  // Reset all mappings
  const resetMappings = () => {
    if (csvPreview) {
      const emptyMappings: CsvMapping[] = csvPreview.headers.map(header => ({
        csvColumn: header,
        assetField: '',
        fieldType: 'basic',
        transform: 'none'
      }));
      setMappings(emptyMappings);
    }
  };

  // Skip all unmapped columns (remove them from mappings)
  const skipUnmapped = () => {
    setMappings(mappings.filter(m => m.assetField && m.assetField !== ''));
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  // If selection was passed from BulkOperationBar, pre-load it and switch to Export tab
  useEffect(() => {
    try {
      const raw = localStorage.getItem('csvExportSelectedAssetIds');
      if (raw) {
        setActiveTab('export');
        // keep the IDs for the export call
      }
    } catch {}
  }, []);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadSavedTemplates(),
        loadTemplates(),
        loadCategories(),
        buildAvailableFields()
      ]);
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load initial data');
    }
  };

  const loadSavedTemplates = async () => {
    try {
      const response = await axiosInstance.get('/api/csv/templates');
      setSavedTemplates(response.data);
    } catch (err) {
      console.error('Error loading saved templates:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await axiosInstance.get('/api/templates');
      setTemplates(response.data);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axiosInstance.get('/api/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const buildAvailableFields = async () => {
    try {
      const response = await axiosInstance.get('/api/csv/available-fields');
      console.log('Debug - Available fields from API:', response.data);
      setAvailableFields(response.data);
    } catch (err) {
      console.error('Error loading available fields:', err);
      // Fallback to basic fields if API fails
      const basicFields: AvailableField[] = [
        { id: 'title', name: 'Title', type: 'basic', description: 'Asset title', isKey: true },
        { id: 'description', name: 'Description', type: 'basic', description: 'Asset description', isKey: false },
        { id: 'fileName', name: 'File Name', type: 'basic', description: 'Original file name', isKey: true },
        { id: 'qbankId', name: 'Qbank ID', type: 'basic', description: 'Legacy/external reference id', isKey: true },
        { id: 'qbankCreationDate', name: 'Qbank Creation Date', type: 'basic', description: 'Legacy Qbank creation timestamp', isKey: false },
        { id: 'wordpressPublishedAt', name: 'WordPress Published Date', type: 'basic', description: 'WordPress publication timestamp', isKey: false }
      ];
      setAvailableFields(basicFields);
    }
  };

  const refreshCsvPreview = async (file: File) => {
    if (!file) return;

    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('delimiter', importSettings.delimiter);
      formData.append('encoding', importSettings.encoding);
      formData.append('hasHeader', importSettings.hasHeader.toString());

      const response = await axiosInstance.post('/api/csv/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const previewData = response.data as CsvPreviewData;
      setCsvPreview(previewData);

      if (selectedTemplate?.type === 'import') {
        applyImportTemplateMappings(selectedTemplate, previewData, { preserveExistingCsvMappings: true });
        return;
      }
      
      // Auto-generate basic mappings if headers match known fields
      if (previewData.headers && importSettings.hasHeader) {
        console.log('Debug - Auto-mapping with:', {
          headers: previewData.headers,
          availableFieldsCount: availableFields.length,
          availableFields: availableFields.map(f => ({ id: f.id, name: f.name, fieldLabel: f.fieldLabel, fieldName: f.fieldName }))
        });
        
        const autoMappings: CsvMapping[] = previewData.headers.map((header: string) => {
          // Simple matching - just try to find exact matches
          let matchingField = availableFields.find(field => {
            const headerLower = header.toLowerCase().trim();
            const fieldNameLower = field.name.toLowerCase().trim();
            
            // Remove template suffix for comparison
            const fieldNameWithoutTemplate = fieldNameLower.replace(/\s*\([^)]*\)\s*$/, '');
            
            return fieldNameWithoutTemplate === headerLower;
          });

          console.log(`Debug - Header "${header}" matched to field:`, matchingField ? { id: matchingField.id, name: matchingField.name } : 'No match');

          return {
            csvColumn: header,
            assetField: matchingField?.id || '',
            fieldType: matchingField?.type || 'basic',
            transform: matchingField?.fieldType === 'list' ? 'split' : 'none'
          };
        });
        setMappings(autoMappings);
      }
    } catch (err: any) {
      setError(`Failed to preview CSV: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFile(file);
    
    // Ensure available fields are loaded before processing
    if (availableFields.length === 0) {
      await buildAvailableFields();
    }
    
    await refreshCsvPreview(file);
  };

  // Auto-refresh preview when import settings change
  useEffect(() => {
    if (csvFile && availableFields.length > 0) {
      refreshCsvPreview(csvFile);
    }
  }, [importSettings.delimiter, importSettings.encoding, importSettings.hasHeader, availableFields]);

  const handleMappingChange = (index: number, field: keyof CsvMapping, value: any) => {
    const newMappings = [...mappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    
    // Auto-detect field type when asset field changes
    if (field === 'assetField') {
      const matchingField = availableFields.find(f => f.id === value);
      if (matchingField) {
        newMappings[index].fieldType = matchingField.type;
        if (matchingField.fieldType === 'list' && (!newMappings[index].transform || newMappings[index].transform === 'none')) {
          newMappings[index].transform = 'split';
        }
      }
    }
    
    // PREVENT number transform for GTIN/EAN fields
    if (field === 'transform' && value === 'number') {
      const fieldName = newMappings[index].assetField.toLowerCase();
      const isGTINField = fieldName.includes('gtin') || 
                          fieldName.includes('ean') || 
                          fieldName.includes('barcode');
      
      if (isGTINField) {
        alert('⚠️ GTIN/EAN fields cannot use number transform as it removes leading zeros. Using "none" instead.');
        newMappings[index].transform = 'none';
      }
    }
    
    setMappings(newMappings);
  };

  const addMapping = () => {
    setMappings([...mappings, {
      csvColumn: '',
      assetField: '',
      fieldType: 'basic',
      transform: 'none'
    }]);
  };

  const removeMapping = (index: number) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  const closeTemplateDialog = () => {
    setIsTemplateDialogOpen(false);
    setTemplateName('');
    setTemplateDescription('');
    setEditingTemplate(null);
  };

  const openSaveTemplateDialog = () => {
    setEditingTemplate(null);
    setTemplateName('');
    setTemplateDescription('');
    setIsTemplateDialogOpen(true);
  };

  const openUpdateSelectedTemplateDialog = () => {
    if (!selectedTemplate) return;

    setEditingTemplate(selectedTemplate);
    setTemplateName(selectedTemplate.name);
    setTemplateDescription(selectedTemplate.description);
    setIsTemplateDialogOpen(true);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      setError('Template name is required');
      return;
    }

    // Filter out mappings with empty assetField (unmapped columns)
    const validMappings = mappings.filter(m => m.assetField && m.assetField.trim() !== '');
    
    if (activeTab === 'import' && validMappings.length === 0) {
      setError('At least one field mapping is required. Please map at least one CSV column to an asset field.');
      return;
    }

    try {
      const templateData: SavedTemplate = {
        name: templateName,
        description: templateDescription,
        type: activeTab,
        mappings: activeTab === 'import' ? validMappings : [],
        settings: activeTab === 'import' ? {
          delimiter: importSettings.delimiter,
          encoding: importSettings.encoding,
          hasHeader: importSettings.hasHeader
        } : {
          delimiter: exportSettings.delimiter,
          encoding: exportSettings.encoding,
          hasHeader: true,
          dateFormat: exportSettings.dateFormat,
          selectedFields: exportSettings.selectedFields
        }
      };

      if (editingTemplate?._id) {
        const response = await axiosInstance.put(`/api/csv/templates/${editingTemplate._id}`, templateData);
        setSelectedTemplate(response.data);
        setSuccess('Template updated successfully');
      } else {
        const response = await axiosInstance.post('/api/csv/templates', templateData);
        setSelectedTemplate(response.data);
        setSuccess('Template saved successfully');
      }

      await loadSavedTemplates();
      closeTemplateDialog();
    } catch (err: any) {
      setError(`Failed to save template: ${err.response?.data?.message || err.message}`);
    }
  };

  const loadTemplate = (template: SavedTemplate) => {
    console.log('Debug - Loading template:', template);
    setSelectedTemplate(template);
    
    if (template.type === 'import') {
      setImportSettings(previous => ({
        ...previous,
        delimiter: template.settings.delimiter,
        encoding: template.settings.encoding,
        hasHeader: template.settings.hasHeader
      }));
      applyImportTemplateMappings(template, csvPreview, { preserveExistingCsvMappings: false });
    } else {
      setMappings(template.mappings);
      setExportSettings(previous => ({
        ...previous,
        delimiter: template.settings.delimiter,
        encoding: template.settings.encoding,
        dateFormat: template.settings.dateFormat || 'YYYY-MM-DD',
        selectedFields: Array.isArray(template.settings.selectedFields) ? template.settings.selectedFields : previous.selectedFields
      }));
    }
    
    setSuccess(`Template "${template.name}" loaded successfully`);
  };

  const deleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await axiosInstance.delete(`/api/csv/templates/${templateId}`);
      await loadSavedTemplates();
      setSuccess('Template deleted successfully');
      
      if (selectedTemplate?._id === templateId) {
        setSelectedTemplate(null);
      }
    } catch (err: any) {
      setError(`Failed to delete template: ${err.response?.data?.message || err.message}`);
    }
  };

  const executeImport = async () => {
    if (!csvFile || mappings.length === 0) {
      setError('Please select a file and configure field mappings');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('settings', JSON.stringify(importSettings));

      const response = await axiosInstance.post('/api/csv/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setProgress(progress);
        }
      });

      setResults(response.data);
      const skippedPart = response.data.skipped ? `, ${response.data.skipped} skipped` : '';
      const warningsPart = response.data.warnings?.length ? `, ${response.data.warnings.length} warnings` : '';
      setSuccess(`Import completed: ${response.data.successful} successful, ${response.data.failed} failed${skippedPart}${warningsPart}`);
    } catch (err: any) {
      setError(`Import failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const executeExport = async () => {
    // For export we rely on exportSettings.selectedFields, not the import mappings table
    if (exportSettings.selectedFields.length === 0) {
      setError('Please select at least one field to export');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      let assetIds: string[] | undefined = undefined;
      try {
        const raw = localStorage.getItem('csvExportSelectedAssetIds');
        if (raw) assetIds = JSON.parse(raw);
      } catch {}

      // Build mappings using friendly labels for headers
      const fieldById = new Map(availableFields.map(f => [f.id, f] as const));
      const builtMappings = exportSettings.selectedFields.map((fieldId: string) => {
        const f = fieldById.get(fieldId);
        const isMetadata = fieldId.startsWith('metadata.');
        const fieldType: 'basic' | 'metadata' | 'category' | 'tag' = isMetadata
          ? 'metadata'
          : (fieldId === 'categories' ? 'category' : (fieldId === 'tags' || fieldId === 'aiTags' ? 'tag' : 'basic'));
        // Prefer pure label for metadata, else use provided friendly name
        let headerLabel = '';
        if (f) {
          if (fieldType === 'metadata') {
            headerLabel = (f.fieldLabel || f.name || fieldId).toString();
            // Strip any template suffix like " (Produkter)"
            headerLabel = headerLabel.replace(/\s*\([^)]*\)\s*$/, '');
          } else {
            headerLabel = (f.name || fieldId).toString();
          }
        } else {
          headerLabel = isMetadata ? fieldId.replace(/^metadata\./, '') : fieldId;
        }
        return {
          csvColumn: headerLabel,
          assetField: fieldId,
          fieldType
        } as CsvMapping;
      });

      const response = await axiosInstance.post('/api/csv/export', {
        // Send explicit mappings with label headers
        mappings: builtMappings,
        settings: exportSettings,
        assetIds
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assets_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Export completed successfully');
      // Clear stored selection after successful export
      try { localStorage.removeItem('csvExportSelectedAssetIds'); } catch {}
    } catch (err: any) {
      setError(`Export failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderImportTab = () => {
    const stats = getMappingStats();
    const currentStep = !csvFile ? 0 : !csvPreview ? 0 : stats.mapped === 0 ? 1 : 2;
    
    // Get available key fields (only mapped fields can be key fields)
    const availableKeyFields = mappings
      .filter(m => m.assetField && m.assetField !== '')
      .map(m => {
        const field = availableFields.find(f => f.id === m.assetField);
        return field ? { id: m.assetField, name: field.name, csvColumn: m.csvColumn } : null;
      })
      .filter(Boolean) as { id: string; name: string; csvColumn: string }[];

    return (
      <Box>
        {/* Progress Stepper */}
        <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
          <Step completed={!!csvFile}>
            <StepLabel>Upload CSV</StepLabel>
          </Step>
          <Step completed={stats.mapped > 0}>
            <StepLabel>Map Fields</StepLabel>
          </Step>
          <Step>
            <StepLabel>Import</StepLabel>
          </Step>
        </Stepper>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* File Upload Section */}
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-upload"
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="csv-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<UploadIcon />}
                  size="large"
                >
                  Choose CSV File
                </Button>
              </label>
              {csvFile && (
                <Chip 
                  label={csvFile.name} 
                  onDelete={() => {
                    setCsvFile(null);
                    setCsvPreview(null);
                    setMappings([]);
                  }}
                  color="primary"
                  variant="outlined"
                />
              )}
              {csvPreview && (
                <Chip 
                  label={`${csvPreview.totalRows} rows`}
                  color="success"
                  size="small"
                />
              )}
            </Box>

            {/* Settings in two columns */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {/* Format Settings */}
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon fontSize="small" />
                  Format Settings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Delimiter"
                      value={importSettings.delimiter}
                      onChange={(e) => setImportSettings({...importSettings, delimiter: e.target.value})}
                      size="small"
                      sx={{ width: 120 }}
                      helperText="Usually , or ;"
                      FormHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Encoding</InputLabel>
                      <Select
                        value={importSettings.encoding}
                        onChange={(e) => setImportSettings({...importSettings, encoding: e.target.value})}
                        label="Encoding"
                      >
                        <MenuItem value="utf-8">UTF-8</MenuItem>
                        <MenuItem value="iso-8859-1">ISO-8859-1</MenuItem>
                        <MenuItem value="windows-1252">Windows-1252</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importSettings.hasHeader}
                        onChange={(e) => setImportSettings({...importSettings, hasHeader: e.target.checked})}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">First row contains headers</Typography>}
                  />
                </Box>
              </Box>

              {/* Import Behavior */}
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UploadIcon fontSize="small" />
                  Import Behavior
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importSettings.updateExisting}
                        onChange={(e) => setImportSettings({...importSettings, updateExisting: e.target.checked})}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Update existing assets</Typography>
                        <Typography variant="caption" color="text.secondary">Match and update assets by key field</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importSettings.createMissing}
                        onChange={(e) => setImportSettings({...importSettings, createMissing: e.target.checked})}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">Create new assets</Typography>
                        <Typography variant="caption" color="text.secondary">Create placeholders for unmatched rows</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importSettings.dryRun}
                        onChange={(e) => setImportSettings({...importSettings, dryRun: e.target.checked})}
                        size="small"
                        color="warning"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" color={importSettings.dryRun ? 'warning.main' : 'inherit'}>
                          🧪 Dry-run mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Simulate without making changes</Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            </Box>

            {/* Warning messages */}
            {importSettings.updateExisting && !importSettings.createMissing && (
              <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
                Only existing assets will be updated. Rows without matches will be skipped.
              </Alert>
            )}
            {importSettings.dryRun && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Dry-run enabled:</strong> No actual changes will be made. Use this to test your import settings.
              </Alert>
            )}
          </Paper>

          {/* CSV Preview */}
          {csvPreview && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  CSV Preview
                </Typography>
                <Chip 
                  label={`${csvPreview.headers.length} columns × ${csvPreview.totalRows} rows`}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <TableContainer sx={{ maxHeight: 250, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f5f5f5', fontWeight: 600, minWidth: 40 }}>#</TableCell>
                      {csvPreview.headers.map((header, index) => (
                        <TableCell key={index} sx={{ bgcolor: '#f5f5f5', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvPreview.rows.slice(0, 8).map((row, rowIndex) => (
                      <TableRow key={rowIndex} hover>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{rowIndex + 1}</TableCell>
                        {row.map((cell, cellIndex) => (
                          <TableCell key={cellIndex} sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cell || <Typography variant="caption" color="text.disabled">empty</Typography>}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {csvPreview.totalRows > 8 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing first 8 rows of {csvPreview.totalRows}
                </Typography>
              )}
            </Paper>
          )}

          {/* Field Mapping */}
          {csvPreview && (
            <Paper sx={{ p: 3 }}>
              {/* Header with stats and actions */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Field Mapping
                  </Typography>
                  {/* Mapping Stats */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label={`${stats.mapped} mapped`}
                      color="success"
                      size="small"
                      variant={stats.mapped > 0 ? 'filled' : 'outlined'}
                    />
                    {stats.unmapped > 0 && (
                      <Chip 
                        icon={<WarningIcon />}
                        label={`${stats.unmapped} unmapped`}
                        color="warning"
                        size="small"
                      />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {stats.total} total columns
                    </Typography>
                  </Box>
                </Box>
                
                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Reset all mappings">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={resetMappings}
                    >
                      Reset
                    </Button>
                  </Tooltip>
                  {stats.unmapped > 0 && (
                    <Tooltip title="Remove all unmapped columns">
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<ClearIcon />}
                        onClick={skipUnmapped}
                      >
                        Skip Unmapped
                      </Button>
                    </Tooltip>
                  )}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addMapping}
                    variant="outlined"
                    size="small"
                  >
                    Add Row
                  </Button>
                </Box>
              </Box>

              {/* Key Field Selection */}
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                bgcolor: '#e3f2fd', 
                borderRadius: 1,
                border: '1px solid #90caf9'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🔑 Match assets by:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 250 }}>
                    <Select
                      value={importSettings.keyField}
                      onChange={(e) => setImportSettings({...importSettings, keyField: e.target.value})}
                      displayEmpty
                    >
                      {availableKeyFields.length === 0 ? (
                        <MenuItem value="" disabled>
                          <em>Map at least one field first</em>
                        </MenuItem>
                      ) : (
                        availableKeyFields.map(field => (
                          <MenuItem key={field.id} value={field.id}>
                            {field.name} (from "{field.csvColumn}")
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary">
                    This field will be used to find and match existing assets
                  </Typography>
                </Box>
              </Box>

              {/* Collapsible Transform Help */}
              <Accordion 
                expanded={transformHelpExpanded} 
                onChange={() => setTransformHelpExpanded(!transformHelpExpanded)}
                sx={{ mb: 2, bgcolor: '#fafafa' }}
                disableGutters
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpIcon fontSize="small" color="action" />
                    <Typography variant="body2">Transform Options Help</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600}>None</Typography>
                      <Typography variant="caption" color="text.secondary">Use the CSV value exactly as it is</Typography>
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600}>Split</Typography>
                      <Typography variant="caption" color="text.secondary">"A,B,C" → ["A","B","C"] (for tags/categories)</Typography>
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600}>Parse Date</Typography>
                      <Typography variant="caption" color="text.secondary">"2024-01-15" → Date object</Typography>
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600}>Parse Number</Typography>
                      <Typography variant="caption" color="text.secondary">"123.45" → 123.45</Typography>
                    </Box>
                    <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600}>Parse Boolean</Typography>
                      <Typography variant="caption" color="text.secondary">"ja"/"1" → true, "nej"/blankt → false</Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Mapping Table */}
              <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, width: '25%' }}>CSV Column</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '35%' }}>Map to Asset Field</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '20%' }}>Transform</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15%' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '5%' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappings.map((mapping, index) => {
                      const isUnmapped = !mapping.assetField || mapping.assetField === '';
                      const mappedField = availableFields.find(f => f.id === mapping.assetField);
                      const rowBgColor = getMappingRowColor(mapping);
                      const rowBorderColor = getMappingRowBorder(mapping);
                      const isKeyField = importSettings.keyField === mapping.assetField;
                      
                      return (
                        <TableRow 
                          key={index}
                          sx={{ 
                            bgcolor: rowBgColor,
                            borderLeft: `4px solid ${rowBorderColor}`,
                            '&:hover': { bgcolor: isUnmapped ? '#fff3e0' : rowBgColor }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isKeyField && <Typography>🔑</Typography>}
                              <Typography variant="body2" fontWeight={500}>
                                {mapping.csvColumn}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              options={availableFields}
                              value={mappedField || null}
                              onChange={(_, newValue) => {
                                handleMappingChange(index, 'assetField', newValue?.id || '');
                              }}
                              getOptionLabel={(option) => `${option.name}${option.templateName ? ` (${option.templateName})` : ''}`}
                              groupBy={(option) => option.type === 'metadata' ? (option.templateName || 'Metadata') : option.type.charAt(0).toUpperCase() + option.type.slice(1)}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  placeholder={isUnmapped ? "Select field..." : ""}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: 'white'
                                    }
                                  }}
                                />
                              )}
                              renderOption={(props, option) => {
                                const colors = getFieldColor(option);
                                return (
                                  <Box 
                                    component="li" 
                                    {...props}
                                    sx={{ 
                                      borderLeft: `3px solid ${colors.border}`,
                                      bgcolor: colors.bg,
                                      '&:hover': { bgcolor: colors.bg }
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="body2">{option.name}</Typography>
                                      {option.templateName && (
                                        <Typography variant="caption" color="text.secondary">
                                          {option.templateName}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              }}
                              isOptionEqualToValue={(option, value) => option.id === value?.id}
                              sx={{ minWidth: 200 }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={mapping.transform}
                                onChange={(e) => handleMappingChange(index, 'transform', e.target.value)}
                                sx={{ bgcolor: 'white' }}
                              >
                                <MenuItem value="none">None</MenuItem>
                                <MenuItem value="split">Split (comma)</MenuItem>
                                <MenuItem value="date">Parse Date</MenuItem>
                                <MenuItem value="number">Parse Number</MenuItem>
                                <MenuItem value="boolean">Parse Boolean</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            {isUnmapped ? (
                              <Chip label="—" size="small" variant="outlined" />
                            ) : (
                              <Chip 
                                label={mapping.fieldType} 
                                size="small"
                                sx={{
                                  bgcolor: mappedField ? getFieldColor(mappedField).bg : undefined,
                                  borderColor: mappedField ? getFieldColor(mappedField).border : undefined,
                                  border: '1px solid'
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => removeMapping(index)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Color Legend */}
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fafafa', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}>
                  Field type colors:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: '#fff3e0', border: '2px solid #ff9800', borderRadius: 0.5 }} />
                    <Typography variant="caption">Unmapped</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: fieldTypeColors.basic.bg, border: `2px solid ${fieldTypeColors.basic.border}`, borderRadius: 0.5 }} />
                    <Typography variant="caption">Basic</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: fieldTypeColors.tag.bg, border: `2px solid ${fieldTypeColors.tag.border}`, borderRadius: 0.5 }} />
                    <Typography variant="caption">Tags</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: fieldTypeColors.category.bg, border: `2px solid ${fieldTypeColors.category.border}`, borderRadius: 0.5 }} />
                    <Typography variant="caption">Categories</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, bgcolor: fieldTypeColors.metadata.bg, border: `2px solid ${fieldTypeColors.metadata.border}`, borderRadius: 0.5 }} />
                    <Typography variant="caption">Metadata</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Execute Import */}
          {csvPreview && stats.mapped > 0 && (
            <Paper sx={{ p: 3, bgcolor: '#f5f5f5' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Ready to Import
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stats.mapped} fields will be imported from {csvPreview.totalRows} rows
                    {importSettings.dryRun && ' (dry-run mode)'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {selectedTemplate?.type === 'import' && (
                    <Button
                      variant="outlined"
                      onClick={openUpdateSelectedTemplateDialog}
                      startIcon={<TemplateIcon />}
                    >
                      Update Template
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={openSaveTemplateDialog}
                    startIcon={<SaveIcon />}
                  >
                    Save as Template
                  </Button>
                  <Button
                    variant="contained"
                    onClick={executeImport}
                    disabled={isProcessing || stats.mapped === 0}
                    startIcon={<UploadIcon />}
                    size="large"
                    color={importSettings.dryRun ? 'warning' : 'primary'}
                  >
                    {isProcessing ? 'Importing...' : (importSettings.dryRun ? 'Run Test Import' : 'Start Import')}
                  </Button>
                </Box>
              </Box>
              {isProcessing && (
                <Box sx={{ mt: 3 }}>
                  <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                    Processing... {progress}%
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    );
  };

  const renderExportTab = () => (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Export Configuration */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Configuration
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box sx={{ minWidth: 300 }}>
              <Typography variant="subtitle2" gutterBottom>
                Field Categories
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportSettings.includeBasicFields}
                      onChange={(e) => setExportSettings({...exportSettings, includeBasicFields: e.target.checked})}
                    />
                  }
                  label="Basic Asset Fields"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportSettings.includeMetadata}
                      onChange={(e) => setExportSettings({...exportSettings, includeMetadata: e.target.checked})}
                    />
                  }
                  label="Template Metadata Fields"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportSettings.includeCategories}
                      onChange={(e) => setExportSettings({...exportSettings, includeCategories: e.target.checked})}
                    />
                  }
                  label="Categories"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={exportSettings.includeTags}
                      onChange={(e) => setExportSettings({...exportSettings, includeTags: e.target.checked})}
                    />
                  }
                  label="Tags"
                />
              </Box>
            </Box>
            
            <Box sx={{ minWidth: 300 }}>
              <Typography variant="subtitle2" gutterBottom>
                Format Settings
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Delimiter"
                  value={exportSettings.delimiter}
                  onChange={(e) => setExportSettings({...exportSettings, delimiter: e.target.value})}
                  size="small"
                />
                <FormControl size="small">
                  <InputLabel>Encoding</InputLabel>
                  <Select
                    value={exportSettings.encoding}
                    onChange={(e) => setExportSettings({...exportSettings, encoding: e.target.value})}
                    label="Encoding"
                  >
                    <MenuItem value="utf-8">UTF-8</MenuItem>
                    <MenuItem value="iso-8859-1">ISO-8859-1</MenuItem>
                    <MenuItem value="windows-1252">Windows-1252</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Date Format"
                  value={exportSettings.dateFormat}
                  onChange={(e) => setExportSettings({...exportSettings, dateFormat: e.target.value})}
                  size="small"
                  helperText="e.g., YYYY-MM-DD, DD/MM/YYYY"
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Field Selection */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="h6">
              Select Fields to Export
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={colorCodeFields}
                  onChange={(e) => setColorCodeFields(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">Color-code fields</Typography>}
              sx={{ mr: 0 }}
            />
          </Box>

          {/* Color Legend */}
          {colorCodeFields && (
            <Box sx={{ 
              mb: 2, 
              p: 1.5, 
              bgcolor: '#fafafa', 
              borderRadius: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Typography variant="caption" sx={{ width: '100%', mb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
                Field type colors:
              </Typography>
              {/* Basic */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors.basic.border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors.basic.bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Basic Fields</Typography>
                </Box>
              </Box>
              {/* Tags */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors.tag.border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors.tag.bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Tags</Typography>
                </Box>
              </Box>
              {/* Categories */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors.category.border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors.category.bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Categories</Typography>
                </Box>
              </Box>
              {/* Metadata templates */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors['metadata:Produkter'].border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors['metadata:Produkter'].bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Produkter</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors['metadata:Vin'].border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors['metadata:Vin'].bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Vin</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors['metadata:Mat'].border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors['metadata:Mat'].bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Mat</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors['metadata:Butiksmaterial'].border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors['metadata:Butiksmaterial'].bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Butiksmaterial</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 4, height: 16, bgcolor: fieldTypeColors.metadata.border, borderRadius: 0.5 }} />
                <Box sx={{ px: 1, py: 0.25, bgcolor: fieldTypeColors.metadata.bg, borderRadius: 0.5 }}>
                  <Typography variant="caption">Other templates</Typography>
                </Box>
              </Box>
            </Box>
          )}
          
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1 }}>
            {availableFields
              .filter(field => {
                if (field.type === 'basic' && !exportSettings.includeBasicFields) return false;
                if (field.type === 'metadata' && !exportSettings.includeMetadata) return false;
                if (field.type === 'category' && !exportSettings.includeCategories) return false;
                if (field.type === 'tag' && !exportSettings.includeTags) return false;
                return true;
              })
              .map(field => {
                const colors = getFieldColor(field);
                return (
                  <Box 
                    key={field.id} 
                    sx={{ 
                      p: 1, 
                      border: '1px solid',
                      borderColor: colorCodeFields ? colors.border + '40' : '#eee',
                      borderLeft: colorCodeFields ? `4px solid ${colors.border}` : '1px solid #eee',
                      borderRadius: 1,
                      bgcolor: colors.bg,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: colorCodeFields ? colors.border + '80' : '#ccc',
                        boxShadow: colorCodeFields ? `0 2px 8px ${colors.border}20` : '0 2px 8px rgba(0,0,0,0.08)'
                      }
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={exportSettings.selectedFields.includes(field.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setExportSettings({
                                ...exportSettings,
                                selectedFields: [...exportSettings.selectedFields, field.id]
                              });
                            } else {
                              setExportSettings({
                                ...exportSettings,
                                selectedFields: exportSettings.selectedFields.filter(f => f !== field.id)
                              });
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{field.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {field.description}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                );
              })}
          </Box>
        </Paper>

        {/* Execute Export */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Execute Export
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={executeExport}
              disabled={isProcessing || exportSettings.selectedFields.length === 0}
              startIcon={<DownloadIcon />}
            >
              {isProcessing ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              variant="outlined"
              onClick={openSaveTemplateDialog}
              startIcon={<SaveIcon />}
            >
              Save as Template
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );

  const renderSavedTemplates = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Saved Templates
      </Typography>
      <Box sx={{ 
        border: '1px solid #e0e0e0', 
        borderRadius: '8px', 
        backgroundColor: '#fff', 
        maxHeight: 300, 
        overflow: 'auto' 
      }}>
        {savedTemplates.filter(template => template.type === activeTab).length === 0 ? (
          <Typography variant="caption" sx={{ p: 2, display: 'block', textAlign: 'center', color: '#999' }}>
            No templates saved yet.
          </Typography>
        ) : (
          savedTemplates
            .filter(template => template.type === activeTab)
            .map(template => (
              <Box 
                key={template._id} 
                sx={{ 
                  p: 1.5, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #f0f0f0',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { background: '#f9f9f9' }
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {template.name}
                    </Typography>
                    <Chip 
                      label={template.type} 
                      size="small" 
                      color={template.type === 'import' ? 'primary' : 'secondary'}
                      sx={{ height: 18 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {template.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {template.mappings.length} field mappings
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Load">
                    <IconButton size="small" onClick={() => loadTemplate(template)}>
                      <TemplateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setEditingTemplate(template);
                        setTemplateName(template.name);
                        setTemplateDescription(template.description);
                        setIsTemplateDialogOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => deleteTemplate(template._id!)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        CSV Import/Export
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Import assets from CSV files or export asset data to CSV format. Save templates for repeated use.
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Import" value="import" />
          <Tab label="Export" value="export" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === 'import' ? renderImportTab() : renderExportTab()}
        </Box>
      </Paper>

      {renderSavedTemplates()}

      {/* Template Save/Edit Dialog */}
      <Dialog open={isTemplateDialogOpen} onClose={closeTemplateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'Update Template' : 'Save Template'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTemplateDialog}>Cancel</Button>
          <Button onClick={saveTemplate} variant="contained">
            {editingTemplate ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New CSV Columns Dialog */}
      <Dialog open={!!pendingTemplateNewColumns} onClose={dismissTemplateNewColumns} maxWidth="sm" fullWidth>
        <DialogTitle>New CSV fields detected</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This CSV file contains columns that are not part of the selected template
            {pendingTemplateNewColumns?.template.name ? ` "${pendingTemplateNewColumns.template.name}"` : ''}.
            Add them as unmapped rows, then map them and update the template if you want to keep them.
          </Alert>
          <List dense>
            {pendingTemplateNewColumns?.missingHeaders.map(header => (
              <ListItem key={header}>
                <ListItemText primary={header} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismissTemplateNewColumns}>Not now</Button>
          <Button onClick={confirmAddTemplateNewColumns} variant="contained">
            Add as Unmapped Rows
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Dialog */}
      {results && (
        <Dialog open={!!results} onClose={() => setResults(null)} maxWidth="md" fullWidth>
          <DialogTitle>Import Results</DialogTitle>
          <DialogContent>
            <Alert severity={results.failed > 0 || results.warnings?.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              Import completed: {results.successful} successful, {results.failed} failed{results.skipped ? `, ${results.skipped} skipped` : ''}{results.warnings?.length ? `, ${results.warnings.length} warnings` : ''}
            </Alert>

            {results.warnings && results.warnings.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Warnings:</Typography>
                <List>
                  {results.warnings.slice(0, 10).map((warning: any, index: number) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`Row ${warning.row}: ${warning.message}`}
                        secondary={warning.data ? JSON.stringify(warning.data) : undefined}
                      />
                    </ListItem>
                  ))}
                </List>
                {results.warnings.length > 10 && (
                  <Typography variant="body2" color="text.secondary">
                    ... and {results.warnings.length - 10} more warnings
                  </Typography>
                )}
              </Box>
            )}
            
            {results.skippedDetails && results.skippedDetails.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Skipped (Not Found):</Typography>
                <List>
                  {results.skippedDetails.slice(0, 10).map((item: any, index: number) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`Row ${item.row}: ${item.reason}`}
                        secondary={`${item.keyField || 'key'} = ${item.keyValue ?? '(empty)'}`}
                      />
                    </ListItem>
                  ))}
                </List>
                {results.skippedDetails.length > 10 && (
                  <Typography variant="body2" color="text.secondary">
                    ... and {results.skippedDetails.length - 10} more skipped
                  </Typography>
                )}
              </Box>
            )}

            {results.details && results.details.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Per‑row details (first {Math.min(results.details.length, 200)} rows)</Typography>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Key</TableCell>
                        <TableCell>Matched</TableCell>
                        <TableCell>Actions (csv → field → status)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.details.slice(0, 50).map((r: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell>{r.row}</TableCell>
                          <TableCell>{r.keyField}: {r.keyValue ?? '(empty)'}</TableCell>
                          <TableCell>{r.matchedAssetId ? `Yes (${r.matchedBy})` : 'No'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {r.actions.map((a: any, i: number) => (
                                <Typography key={i} variant="caption">
                                  {a.csvColumn} → {a.assetField} → {a.status}
                                  {a.reason ? ` (${a.reason})` : ''}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {results.errors && results.errors.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>Errors:</Typography>
                <List>
                  {results.errors.slice(0, 10).map((error: any, index: number) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`Row ${error.row}: ${error.message}`}
                        secondary={error.data ? JSON.stringify(error.data) : undefined}
                      />
                    </ListItem>
                  ))}
                </List>
                {results.errors.length > 10 && (
                  <Typography variant="body2" color="text.secondary">
                    ... and {results.errors.length - 10} more errors
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              try {
                const blob = new Blob([JSON.stringify(results, null, 2)], { type: 'application/json;charset=utf-8' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `csv-import-result-${Date.now()}.json`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              } catch {}
            }}>Export JSON</Button>
            <Button onClick={() => setResults(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbars for success/error messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CsvImportExport; 